import React, { useMemo, useState, useCallback, useRef } from 'react';
import { 
  FloatingBarChartProps, 
  ThemeConfig, 
  PresetTheme,
  BarClickData,
  ChartDimensions,
} from '../types';
import { ChartContainer, Axis, Bar, Legend, Tooltip, Title } from './core';
import { 
  calculateScale, 
  valueToPosition,
} from '../utils/scales';
import { 
  getActiveTheme, 
  mergeAxisConfig, 
  mergeLegendConfig, 
  mergeTooltipConfig, 
  mergeAnimationConfig,
} from '../utils/defaults';
import { getDatasetColor, getHoverColor, resolveColor } from '../utils/colors';

export interface FloatingBarChartComponentProps extends FloatingBarChartProps {
  /** Preset theme name */
  theme?: PresetTheme;
  /** Custom theme configuration */
  themeConfig?: ThemeConfig;
}

/**
 * FloatingBarChart Component
 * 
 * A bar chart where bars can "float" - they don't have to start from zero.
 * Each data point is specified as [start, end] to define the bar's range.
 * Fully responsive with mobile-optimized rendering.
 * 
 * Perfect for:
 * - Gantt-style charts
 * - Range visualization
 * - Showing intervals or periods
 * - Temperature ranges (min/max)
 * 
 * @example
 * ```tsx
 * <FloatingBarChart
 *   labels={['Mon', 'Tue', 'Wed', 'Thu', 'Fri']}
 *   datasets={[
 *     { 
 *       label: 'Working Hours', 
 *       data: [[9, 17], [8, 16], [10, 18], [9, 17], [8, 15]] 
 *     }
 *   ]}
 *   theme="vibrant"
 * />
 * ```
 */
export const FloatingBarChart: React.FC<FloatingBarChartComponentProps> = ({
  labels,
  datasets,
  title,
  xAxis,
  yAxis,
  legend,
  tooltip,
  animation,
  width,
  height = 300,
  padding,
  backgroundColor,
  onBarClick,
  onBarHover,
  className,
  style,
  id,
  ariaLabel,
  borderRadius = 0,
  orientation = 'vertical',
  barGap = 2,
  groupGap = 0.2,
  showValues,
  valueLabels,
  theme = 'default',
  themeConfig,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltipData, setTooltipData] = useState<{
    x: number;
    y: number;
    value: number | [number, number];
    label: string;
    datasetLabel: string;
    color: string;
    mouseX?: number;
    mouseY?: number;
  } | null>(null);
  const [hiddenDatasets, setHiddenDatasets] = useState<Set<number>>(new Set());

  // Get theme
  const activeTheme = useMemo(() => {
    return getActiveTheme(theme, themeConfig);
  }, [theme, themeConfig]);

  const colors = activeTheme.colors || [];

  // Filter visible datasets
  const visibleDatasets = useMemo(() => {
    return datasets.map((ds, i) => ({
      ...ds,
      hidden: ds.hidden || hiddenDatasets.has(i),
    }));
  }, [datasets, hiddenDatasets]);

  // Merge configs
  const xAxisConfig = useMemo(() => mergeAxisConfig(xAxis, undefined, activeTheme), [xAxis, activeTheme]);
  const yAxisConfig = useMemo(() => mergeAxisConfig({ ...yAxis, beginAtZero: false }, undefined, activeTheme), [yAxis, activeTheme]);
  const legendConfig = useMemo(() => mergeLegendConfig(legend, undefined, activeTheme), [legend, activeTheme]);
  const tooltipConfig = useMemo(() => mergeTooltipConfig(tooltip, undefined, activeTheme), [tooltip, activeTheme]);
  const animationConfig = useMemo(() => mergeAnimationConfig(animation), [animation]);

  // Calculate scale for floating data
  const scale = useMemo(() => {
    let dataMin = Infinity;
    let dataMax = -Infinity;

    visibleDatasets.forEach(dataset => {
      if (dataset.hidden) return;
      dataset.data.forEach(value => {
        if (Array.isArray(value)) {
          dataMin = Math.min(dataMin, value[0], value[1]);
          dataMax = Math.max(dataMax, value[0], value[1]);
        } else {
          dataMin = Math.min(dataMin, 0, value);
          dataMax = Math.max(dataMax, 0, value);
        }
      });
    });

    if (dataMin === Infinity) dataMin = 0;
    if (dataMax === -Infinity) dataMax = 100;

    return calculateScale([], {
      min: yAxis?.min ?? dataMin,
      max: yAxis?.max ?? dataMax,
      beginAtZero: false,
      maxTicks: 8,
    });
  }, [visibleDatasets, yAxis]);

  // Handle bar hover with exact cursor position
  const handleBarHover = useCallback((
    e: React.MouseEvent, 
    data: BarClickData | null,
    mousePos?: { x: number; y: number }
  ) => {
    if (data) {
      setTooltipData({
        x: 0,
        y: 0,
        value: data.value,
        label: data.label,
        datasetLabel: data.datasetLabel,
        color: resolveColor(
          datasets[data.datasetIndex].backgroundColor,
          data.dataIndex,
          Array.isArray(data.value) ? data.value[1] : data.value,
          getDatasetColor(data.datasetIndex, colors)
        ),
        mouseX: mousePos?.x,
        mouseY: mousePos?.y,
      });
    } else {
      setTooltipData(null);
    }
    onBarHover?.(e, data);
  }, [datasets, colors, onBarHover]);

  const handleToggleDataset = useCallback((index: number) => {
    setHiddenDatasets(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  // Custom tooltip formatter for floating bars
  const floatingTooltipConfig = useMemo(() => ({
    ...tooltipConfig,
    formatter: tooltipConfig.formatter || ((value: number | [number, number], datasetLabel: string, label: string) => {
      if (Array.isArray(value)) {
        return `${value[0].toLocaleString()} — ${value[1].toLocaleString()}`;
      }
      return value.toLocaleString();
    }),
  }), [tooltipConfig]);

  // Generate accessible description
  const chartDescription = useMemo(() => {
    const ranges = datasets.map(ds => {
      const rangeStr = ds.data.map((v, i) => {
        if (Array.isArray(v)) {
          return `${labels[i]}: ${v[0]} to ${v[1]}`;
        }
        return `${labels[i]}: ${v}`;
      }).join(', ');
      return `${ds.label}: ${rangeStr}`;
    }).join('. ');
    return `Floating bar chart showing ranges. ${ranges}`;
  }, [datasets, labels]);

  const containerWrapperRef = useRef<HTMLDivElement>(null);
  const [chartDimensions, setChartDimensions] = useState<ChartDimensions | null>(null);

  return (
    <div ref={containerWrapperRef} style={{ position: 'relative' }}>
      <ChartContainer
        width={width}
        height={height}
        padding={padding}
        backgroundColor={backgroundColor || activeTheme.backgroundColor}
        className={className}
        style={style}
        id={id}
        ariaLabel={ariaLabel || chartDescription}
        title={typeof title === 'string' ? title : title?.text}
      >
        {(dimensions, containerSvgRef, isMobile) => {
          (svgRef as any).current = containerSvgRef.current;
          // Store dimensions for tooltip
          if (!chartDimensions || chartDimensions.width !== dimensions.width) {
            setTimeout(() => setChartDimensions(dimensions), 0);
          }
        
        const { chartWidth, chartHeight } = dimensions;
        const isVertical = orientation === 'vertical';
        
        // Responsive gaps
        const responsiveBarGap = isMobile ? Math.max(1, barGap * 0.5) : barGap;
        const responsiveGroupGap = isMobile ? Math.max(0.15, groupGap * 0.8) : groupGap;
        
        // Calculate bar dimensions
        const numGroups = labels.length;
        const numBars = visibleDatasets.filter(ds => !ds.hidden).length;
        const categorySize = isVertical ? chartWidth / numGroups : chartHeight / numGroups;
        const groupPadding = categorySize * responsiveGroupGap;
        const availableGroupSize = categorySize - groupPadding;
        const barSize = numBars > 0 ? (availableGroupSize - responsiveBarGap * (numBars - 1)) / numBars : availableGroupSize;

        // Render bars
        const bars: React.ReactNode[] = [];
        let visibleIndex = 0;

        visibleDatasets.forEach((dataset, datasetIndex) => {
          if (dataset.hidden) return;

          dataset.data.forEach((value, dataIndex) => {
            // Get start and end values
            let startValue: number, endValue: number;
            if (Array.isArray(value)) {
              startValue = value[0];
              endValue = value[1];
            } else {
              startValue = 0;
              endValue = value;
            }

            // Calculate position
            const categoryStart = dataIndex * categorySize + groupPadding / 2;
            const barOffset = visibleIndex * (barSize + responsiveBarGap);
            
            let x: number, y: number, barWidth: number, barHeight: number;
            
            if (isVertical) {
              x = categoryStart + barOffset;
              barWidth = barSize;
              
              const startPos = valueToPosition(startValue, scale, chartHeight, true);
              const endPos = valueToPosition(endValue, scale, chartHeight, true);
              
              y = Math.min(startPos, endPos);
              barHeight = Math.abs(startPos - endPos);
            } else {
              y = categoryStart + barOffset;
              barHeight = barSize;
              
              const startPos = valueToPosition(startValue, scale, chartWidth, false);
              const endPos = valueToPosition(endValue, scale, chartWidth, false);
              
              x = Math.min(startPos, endPos);
              barWidth = Math.abs(endPos - startPos);
            }

            // Get colors
            const fillColor = resolveColor(
              dataset.backgroundColor,
              dataIndex,
              endValue,
              getDatasetColor(datasetIndex, colors)
            );
            const strokeColor = resolveColor(dataset.borderColor, dataIndex, endValue, fillColor);
            const hoverFillColor = resolveColor(
              dataset.hoverBackgroundColor,
              dataIndex,
              endValue,
              getHoverColor(fillColor)
            );

            // Get border radius (responsive)
            const baseRadius = dataset.borderRadius ?? borderRadius ?? activeTheme.borderRadius ?? 0;
            const radius = isMobile && typeof baseRadius === 'number' 
              ? Math.max(2, baseRadius * 0.6) 
              : baseRadius;

            bars.push(
              <Bar
                key={`${datasetIndex}-${dataIndex}`}
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                fill={fillColor}
                stroke={strokeColor}
                strokeWidth={dataset.borderWidth || 0}
                borderRadius={radius}
                opacity={dataset.opacity}
                animation={animationConfig}
                animationDelay={(animationConfig.staggerDelay || 0) * (datasetIndex * labels.length + dataIndex)}
                datasetIndex={datasetIndex}
                dataIndex={dataIndex}
                value={value}
                label={labels[dataIndex]}
                datasetLabel={dataset.label}
                onClick={onBarClick}
                onHover={handleBarHover}
                hoverFill={hoverFillColor}
                isMobile={isMobile}
              />
            );
          });

          visibleIndex++;
        });

        return (
          <>
            <Title 
              config={title} 
              dimensions={dimensions}
              defaultColor={activeTheme.textColor}
              defaultFontFamily={activeTheme.fontFamily}
            />

            <Axis
              type="x"
              config={isVertical ? xAxisConfig : yAxisConfig}
              scale={scale}
              dimensions={dimensions}
              labels={isVertical ? labels : undefined}
              orientation={orientation}
              isMobile={isMobile}
            />
            <Axis
              type="y"
              config={isVertical ? yAxisConfig : xAxisConfig}
              scale={scale}
              dimensions={dimensions}
              labels={!isVertical ? labels : undefined}
              orientation={orientation}
              isMobile={isMobile}
            />

            <g className="bars" role="list" aria-label="Floating range bars">
              {bars}
            </g>

            <Legend
              config={legendConfig}
              datasets={visibleDatasets}
              colors={colors}
              dimensions={dimensions}
              onToggleDataset={handleToggleDataset}
              isMobile={isMobile}
            />

          </>
        );
      }}
    </ChartContainer>
      
      {/* Tooltip rendered outside SVG for proper positioning */}
      {floatingTooltipConfig.enabled && tooltipData && chartDimensions && (
        <Tooltip
          config={floatingTooltipConfig}
          data={tooltipData}
          dimensions={chartDimensions}
          containerRef={svgRef}
        />
      )}
    </div>
  );
};

export default FloatingBarChart;
